import React, {ChangeEvent, useMemo, useState} from 'react';
import {EBirdChecklistByFamily, EBirdChecklistBySpecies} from "ebird-mydata-reader";
import {useNavigate, useSearchParams} from "react-router-dom";
import {FamilyExplorerCard} from "../../../components/family-explorer-card";
import {SegmentedFilter} from "../../../components/segmented-filter";
import {getFamilySlug} from "../../../helpers/helpers";
import {toRootRelativeImagePath} from "../../../helpers/imagePaths";
import {
    ChecklistFilter,
    getFamilyExplorerFilter,
    getFamilyCommonName,
    getFamilyImage,
    getFamilyScientificName,
    getFamilyStats,
    setFamilyExplorerFilterParam
} from "../helpers";
import './family-list.scss';

const FAMILY_EXPLORER_ROUTE = '/family-explorer';
const MAX_SPECIES_SEARCH_RESULTS = 8;

type SpeciesSearchResult = {
    family: EBirdChecklistByFamily,
    species: EBirdChecklistBySpecies
}

const normalizeSearchText = (value?: string) => {
    return value
        ?.normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, ' ')
        .trim()
        .replace(/\s+/g, ' ') ?? '';
}

const getEditDistance = (left: string, right: string) => {
    const distances = Array.from({length: left.length + 1}, (_, index) => [index]);

    for (let rightIndex = 0; rightIndex <= right.length; rightIndex++) {
        distances[0][rightIndex] = rightIndex;
    }

    for (let leftIndex = 1; leftIndex <= left.length; leftIndex++) {
        for (let rightIndex = 1; rightIndex <= right.length; rightIndex++) {
            const substitutionCost = left[leftIndex - 1] === right[rightIndex - 1] ? 0 : 1;
            distances[leftIndex][rightIndex] = Math.min(
                distances[leftIndex - 1][rightIndex] + 1,
                distances[leftIndex][rightIndex - 1] + 1,
                distances[leftIndex - 1][rightIndex - 1] + substitutionCost
            );
        }
    }

    return distances[left.length][right.length];
}

const tokenMatches = (queryToken: string, labelToken: string) => {
    if (labelToken === queryToken || labelToken.startsWith(queryToken)) return true;
    if (queryToken.length < 4) return false;

    const allowedDistance = queryToken.length >= 7 ? 2 : 1;
    return getEditDistance(queryToken, labelToken) <= allowedDistance;
}

const fuzzyIncludes = (label: string, query: string) => {
    if (!query) return true;
    if (label.includes(query)) return true;

    const queryTokens = query.split(' ').filter(Boolean);
    const labelTokens = label.split(' ').filter(Boolean);

    return queryTokens.every(queryToken => (
        labelTokens.some(labelToken => tokenMatches(queryToken, labelToken))
    ));
}

const getSpeciesResultImage = (species: EBirdChecklistBySpecies) => {
    return toRootRelativeImagePath(species.image?.thumb ?? species.image?.medium) || '/assets/fallback-bird.svg';
}

export type FamilyListProps = {
    checklist: EBirdChecklistByFamily[],
    checklistStats: {
        seenFamilies: number,
        unseenFamilies: number,
        extinctFamilies: number
    },
    onChangeFamily: (family: EBirdChecklistByFamily) => void
}

export function FamilyList({checklist, checklistStats, onChangeFamily}: FamilyListProps) {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const activeFilter = useMemo(() => getFamilyExplorerFilter(searchParams), [searchParams]);
    const [searchTerm, setSearchTerm] = useState('');
    const visibleFamilies = useMemo(() => {
        const normalizedSearch = normalizeSearchText(searchTerm);

        return checklist.filter(family => {
            const familyStats = getFamilyStats(family);

            if (activeFilter === 'seen' && (!family.seen || familyStats.isExtinct)) return false;
            if (activeFilter === 'unseen' && (family.seen || familyStats.isExtinct)) return false;
            if (activeFilter === 'extinct' && !familyStats.isExtinct) return false;
            if (!normalizedSearch) return true;

            return fuzzyIncludes(
                normalizeSearchText(`${family.familyName} ${family.familyScientific} ${family.familyCommon}`),
                normalizedSearch
            );
        });
    }, [activeFilter, checklist, searchTerm]);
    const speciesSearchResults = useMemo((): SpeciesSearchResult[] => {
        const normalizedSearch = normalizeSearchText(searchTerm);
        if (normalizedSearch.length < 2) return [];

        const matches: SpeciesSearchResult[] = [];
        for (const family of checklist) {
            for (const species of family.speciesList) {
                const searchLabel = normalizeSearchText(`${species.commonName} ${species.scientificName} ${family.familyName} ${family.familyScientific} ${family.familyCommon}`);
                if (!fuzzyIncludes(searchLabel, normalizedSearch)) continue;

                matches.push({family, species});
                if (matches.length >= MAX_SPECIES_SEARCH_RESULTS) {
                    return matches;
                }
            }
        }

        return matches;
    }, [checklist, searchTerm]);

    const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(event.target.value);
    }

    const setActiveFilter = (filter: ChecklistFilter) => {
        setSearchParams(setFamilyExplorerFilterParam(searchParams, filter), {replace: true});
    }

    const openSpeciesResult = ({family, species}: SpeciesSearchResult) => {
        navigate(`${FAMILY_EXPLORER_ROUTE}/family/${getFamilySlug(family.familyName)}/species/${species.speciesCode}`);
    }

    return (
        <>
            <div className="checklist__toolbar">
                <label className="checklist__search checklist__search--with-results">
                    <span>Search species or families</span>
                    <input
                        type="search"
                        value={searchTerm}
                        onChange={handleSearchChange}
                        placeholder="Search species or families..."
                    />
                    {speciesSearchResults.length ? (
                        <div className="checklist-search-results" role="listbox" aria-label="Species matches">
                            {speciesSearchResults.map(result => (
                                <button
                                    type="button"
                                    className="checklist-search-results__item"
                                    key={`${result.family.familyName}-${result.species.speciesCode}`}
                                    onClick={() => openSpeciesResult(result)}
                                >
                                    <img src={getSpeciesResultImage(result.species)} alt="" loading="lazy" />
                                    <span>
                                        <span className="checklist-search-results__common-name">{result.species.commonName}</span>
                                        <span className="checklist-search-results__scientific-name">{result.species.scientificName}</span>
                                    </span>
                                    <span>
                                        <em className={`checklist-search-results__status ${result.species.seen ? 'is-seen' : 'is-unseen'}`}>
                                            {result.species.seen ? 'Seen' : 'Not Seen'}
                                        </em>
                                    </span>
                                </button>
                            ))}
                        </div>
                    ) : null}
                </label>
                <SegmentedFilter
                    activeId={activeFilter}
                    ariaLabel="Checklist filters"
                    className="checklist__tabs"
                    onChange={setActiveFilter}
                    options={[
                        {id: 'all', label: 'All Families'},
                        {id: 'seen', label: `Seen (${checklistStats.seenFamilies.toLocaleString()})`},
                        {id: 'unseen', label: `Not Seen (${checklistStats.unseenFamilies.toLocaleString()})`},
                        {id: 'extinct', label: `Extinct (${checklistStats.extinctFamilies.toLocaleString()})`}
                    ]}
                />
            </div>

            {visibleFamilies.length ?
                <ul className="checklist-list">
                    {visibleFamilies.map(family => {
                        const familyStats = getFamilyStats(family);
                        const familyImage = getFamilyImage(family) ?? family.speciesList[0]?.image;

                        return (
                            <li className="checklist-list__item" key={family.familyName}>
                                <FamilyExplorerCard
                                    subject={family}
                                    seen={family.seen}
                                    title={getFamilyScientificName(family)}
                                    alt={getFamilyCommonName(family)}
                                    subtitle={getFamilyCommonName(family)}
                                    statusLabel={familyStats.isExtinct ? 'Extinct' : undefined}
                                    statusVariant={familyStats.isExtinct ? 'extinct' : undefined}
                                    statCount={`${familyStats.seenSpecies} / ${familyStats.totalSpecies} species`}
                                    img={toRootRelativeImagePath(familyImage?.medium ?? familyImage?.thumb) || '/assets/fallback-bird.svg'}
                                    statProgress={familyStats.totalSpecies ? familyStats.seenSpecies / familyStats.totalSpecies : 0}
                                    onClick={onChangeFamily}
                                />
                            </li>
                        );
                    })}
                </ul> :
                <div className="checklist__empty-state">
                    <strong>No families found</strong>
                    <span>Try a different search or switch family filters.</span>
                </div>
            }
        </>
    );
}
